import { useEffect, type RefObject } from 'react';
import { afterScrollSettles, isPageScrolling, whenIdle } from './BentoScroll';

// useBentoWarmup — gets the bento's first raster out of the first scroll
// (round 5b critique, 2026-09-25).
//
// On a cold GPU cache the first wheel tick into the bento froze the page for
// 0.7-1.4 s. The clips, the WebGL hero and any single card were ruled out:
// hiding the whole bento took the scroll to 52-60 fps, hiding any one card
// did not. A GPU trace of the tick shows why: ~25 GPU programs built inside
// it, against ~6 with the bento hidden (the ring's mask, the gradients, the
// grain's overlay blend, the shadows, the rounded clips, the compositor's
// programs for the rising cards), each built the first time anything draws
// that recipe. Painting the cards ahead of time at 0.001 opacity far below
// the fold did nothing (BentoReveal): Chrome does not raster content that
// far off screen.
//
// So the row of cards the first scroll brings in is drawn ON screen once,
// while the visitor is still reading the hero: a copy at 1% opacity for two
// frames. On screen, Chrome has to raster it and build its programs then,
// instead of in the middle of the scroll. It is the same GPU work, so the
// page still stops for it (0.8-1.2 s on the loaded test machine); it is
// placed where a stop shows least: right after the hero's canvas takes over,
// while its 3D screens still sit exactly on their stills and before they
// start to move (heroScene LIVE_DELAY_MS, 0.95 s), rather than in a scroll
// that stops answering the wheel. Measured on the production build, 1440 x
// 900, cold profile, first 100 px tick: worst frame 118-184 ms with the
// warm-up, 417-884 ms without it, 50-150 ms with no bento at all.
//
// It only works if the copy is drawn the way the scroll will draw the
// cards; each difference is programs the scroll then builds anyway:
//   * Full size. A quarter-size copy built ~50 programs, yet the tick still
//     built 15 of its 25: images, blurs and gradients take other paths at
//     another scale.
//   * Each card rising the way the reveal raises it (BentoReveal: its own
//     compositor layer, fading, moving by fractions of a pixel), so the
//     compositor's programs for a rising card are built too.
//   * At rest. Showing the hover's spot and glare as well built ~8 more
//     programs and left the tick no better.
//   * One row. Warming every row made the stop 1-2.7 s long for no gain in
//     the first tick. It did take ticks 6-20 from ~37 programs to ~12, but
//     those come in a few at a time once the page is already moving, and
//     a 2.7 s stop right after the hero comes alive is worse.
//
// The copy is the grid's own DOM (cloneNode), so it carries exactly the
// cards' recipes (classes, inline accents, the grain, the mask, the CTA's
// layer) with no second React render and none of the cards' hooks (tilt,
// clips, reveal). Videos become their poster images (a clip's first frame
// is a separate cost, which the clips already keep out of the scroll). It is
// aria-hidden, inert and out of the pointer's way, and it runs once per
// page.

/** Enough to be drawn, too faint to be seen for the two frames it lives. */
const WARM_OPACITY = 0.01;
/** A page with no WebGL scene coming never leaves data-handoff="off"; this
 *  long after mount, "off" is taken to mean that. */
const NO_SCENE_GRACE_MS = 1500;
/** Longest the warm-up waits for the hero's canvas to take over. */
const HANDOFF_WAIT_MAX_MS = 8000;
/** Longest it then waits for an idle moment: short, so the stop lands
 *  before the hero's screens start to move. */
const IDLE_TIMEOUT_MS = 300;
/** BentoReveal's card entrance, which the copy mirrors. */
const RISE_PX = 56;
const RISE_MS = 1000;
const EXPO_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';

let warmed = false;

/** Runs `fn` once the hero's WebGL canvas has taken over from its stills
 *  (so the warm-up never competes with the scene's own set-up), or once it
 *  is clear no scene is coming. Returns the cancel. */
function afterHeroHandoff(fn: () => void): () => void {
  const screens = document.querySelector<HTMLElement>('.hero-screens');
  if (!screens || typeof MutationObserver === 'undefined') {
    fn();
    return () => {};
  }
  const t0 = performance.now();
  let over = false;
  const stop = () => {
    over = true;
    mo.disconnect();
    window.clearTimeout(grace);
    window.clearTimeout(cap);
  };
  const finish = () => {
    if (over) return;
    stop();
    fn();
  };
  const check = () => {
    const state = screens.getAttribute('data-handoff');
    if (state === 'on' || (state !== 'hold' && performance.now() - t0 >= NO_SCENE_GRACE_MS)) finish();
  };
  const mo = new MutationObserver(check);
  mo.observe(screens, { attributes: true, attributeFilter: ['data-handoff'] });
  const grace = window.setTimeout(check, NO_SCENE_GRACE_MS);
  const cap = window.setTimeout(finish, HANDOFF_WAIT_MAX_MS);
  check();
  return stop;
}

/**
 * The cards of the first row the visitor has not fully seen yet: the row
 * the first scroll brings in (by layout, so it follows the breakpoint: the
 * corrido and TikTok cards on desktops, the corrido card alone on phones).
 * A row wholly on screen, or scrolled past, has had its raster. Empty when
 * there is none.
 */
function nextRow(grid: HTMLElement): number[] {
  const rows = new Map<number, number[]>();
  Array.from(grid.children).forEach((li, i) => {
    const top = Math.round(li.getBoundingClientRect().top);
    rows.set(top, [...(rows.get(top) ?? []), i]);
  });
  const ordered = Array.from(rows.entries()).sort((a, b) => a[0] - b[0]).map(([, row]) => row);
  return ordered.find((row) => row.some((i) => grid.children[i].getBoundingClientRect().bottom > window.innerHeight)) ?? [];
}

const STRIP = ['id', 'data-testid', 'data-reveal', 'data-cursor', 'data-pressed', 'aria-describedby'];
const INHERITED = ['color', 'font-family', 'font-size', 'line-height', 'letter-spacing', 'text-align'] as const;

/**
 * A copy of `grid` that shows only the cards in `row`, laid out exactly like
 * the grid and placed so that row sits at the top of the screen. Null when
 * the grid has no size.
 */
function buildRow(section: HTMLElement, grid: HTMLElement, row: number[]): HTMLElement | null {
  const box = grid.getBoundingClientRect();
  const first = grid.children[row[0]];
  if (!box.width || !box.height || !first) return null;
  const copy = grid.cloneNode(true) as HTMLElement;
  // Posters instead of clips. Swapped before the copy is ever attached.
  copy.querySelectorAll('video').forEach((video) => {
    const img = document.createElement('img');
    img.alt = '';
    img.className = video.className;
    const style = video.getAttribute('style');
    if (style) img.setAttribute('style', style);
    const poster = video.getAttribute('poster');
    if (poster) img.src = poster;
    video.replaceWith(img);
  });
  [copy, ...Array.from(copy.querySelectorAll('*'))].forEach((n) => {
    STRIP.forEach((a) => n.removeAttribute(a));
  });
  // A card under the pointer carries the tilt's transform.
  copy.querySelectorAll<HTMLElement>('.tc-card').forEach((card) => {
    card.style.transform = '';
    card.style.willChange = '';
  });
  Array.from(copy.children).forEach((node, i) => {
    const li = node as HTMLElement;
    if (!row.includes(i)) {
      // Kept for the layout, never drawn.
      li.style.visibility = 'hidden';
      return;
    }
    // Also clears the inline opacity 0 of a card still waiting for its
    // entrance (BentoReveal).
    li.style.opacity = String(WARM_OPACITY);
    if (typeof li.animate !== 'function') {
      li.style.transform = 'translate3d(0, 0.5px, 0)';
      li.style.willChange = 'transform, opacity';
    }
  });

  const wrap = document.createElement('div');
  wrap.className = 'tc-warm';
  wrap.setAttribute('aria-hidden', 'true');
  wrap.setAttribute('inert', '');
  // What the section passes down to its cards.
  const cs = getComputedStyle(section);
  INHERITED.forEach((p) => wrap.style.setProperty(p, cs.getPropertyValue(p)));
  Object.assign(wrap.style, {
    position: 'fixed',
    left: `${box.left}px`,
    top: `${box.top - first.getBoundingClientRect().top}px`,
    zIndex: '2147483000',
    width: `${box.width}px`,
    margin: '0',
    pointerEvents: 'none',
  });
  wrap.appendChild(copy);
  return wrap;
}

/** Starts the shown cards of an attached copy rising, as the reveal raises
 *  a card (see buildRow). */
function rise(wrap: HTMLElement) {
  wrap.querySelectorAll<HTMLElement>(':scope > ul > li').forEach((li) => {
    if (li.style.visibility === 'hidden' || typeof li.animate !== 'function') return;
    try {
      li.animate(
        [
          { opacity: WARM_OPACITY, transform: `translate3d(0, ${RISE_PX}px, 0)` },
          { opacity: WARM_OPACITY, transform: 'translate3d(0, 0, 0)' },
        ],
        { duration: RISE_MS, easing: EXPO_OUT, fill: 'both' },
      );
    } catch { /* drawn in place: most of the warm-up still happens */ }
  });
}

/** Draws a copy of the row of cards the first scroll brings in, on screen
 *  for two frames, once, right after the hero has handed off (see above). */
export function useBentoWarmup(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const section = root.current;
    if (warmed || !section || typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') return undefined;
    // No layout (jsdom): nothing to warm.
    if (!section.getBoundingClientRect().width) return undefined;

    let cancel: (() => void) | null = null;
    let frame = 0;
    let wrap: HTMLElement | null = null;

    const run = () => {
      cancel = null;
      if (warmed) return;
      if (isPageScrolling()) {
        cancel = afterScrollSettles(() => { cancel = whenIdle(run, IDLE_TIMEOUT_MS); });
        return;
      }
      if (document.visibilityState === 'hidden') {
        const onShow = () => {
          if (document.visibilityState === 'hidden') return;
          document.removeEventListener('visibilitychange', onShow);
          cancel = whenIdle(run, IDLE_TIMEOUT_MS);
        };
        document.addEventListener('visibilitychange', onShow);
        cancel = () => document.removeEventListener('visibilitychange', onShow);
        return;
      }
      const grid = section.querySelector<HTMLElement>('ul');
      if (!grid) return;
      warmed = true;
      const row = nextRow(grid);
      wrap = row.length ? buildRow(section, grid, row) : null;
      if (!wrap) return;
      document.body.appendChild(wrap);
      rise(wrap);
      frame = window.requestAnimationFrame(() => {
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          wrap?.remove();
          wrap = null;
        });
      });
    };

    cancel = afterHeroHandoff(() => { cancel = whenIdle(run, IDLE_TIMEOUT_MS); });
    return () => {
      cancel?.();
      if (frame) window.cancelAnimationFrame(frame);
      wrap?.remove();
      wrap = null;
    };
  }, [root]);
}
